/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { PageSEO } from "@/shared/seo";
import { withAuthRedirect } from "@/shared/ui";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { BuildingIcon } from "@/shared/ui/components/basic/icons";

import { PageHeader } from "../components/PageHeader";
import { StickyHeaderBar } from "../components/StickyHeaderBar";
import { getListingPageLayout } from "@/shared/ui/layout/listing-page-layout";
import { FilterPanel } from "../components/FilterPanel";
import { PropertyDrawer } from "../components/PropertyDrawer";
import { CrudProvider, useCrudStore } from "../context/PropertyManagementContext";
import type { PropertyFilters } from "../context/PropertyManagementContext";
import { agencyContext, useAgency } from "@pages/authenticated/agency-pages/AgenciesContext";
import { useLayout } from "@layout/app/LayoutContext";
import { SideMenuLink } from "@layout/app/side-menu/SideMenuLink";
import type { PropertyResponseType } from "@services/housien/types";
import { useIsMobile } from "@/shared/hooks/use-is-mobile";
import { useUrlFilters } from "@/shared/hooks/use-url-filters";
import {
  buildAdvancedFilterQuery,
  buildFilterChipLabels,
  removeAdvancedFilterByLabel,
  mapFilterPanelToStore,
  countActiveFilters,
} from "./manage-properties-filters";
import { buildPropertyColumns } from "./manage-properties-columns";
import { usePropertiesData } from "./use-properties-data";
import { PropertiesListBody } from "./PropertiesListBody";
import { usePropertyGrouping } from "./use-property-grouping";
import { usePropertyGroupingData } from "./use-property-grouping-data";
import { groupFilterPatch } from "./manage-properties-filters";
import { PageHeaderGroupByMenu } from "../components/page-header/PageHeaderGroupByMenu";
import { PageHeaderSortMenu } from "../components/page-header/PageHeaderSortMenu";
import { PageHeaderViewToggle } from "../components/page-header/PageHeaderViewToggle";
import { RowsIcon, DashboardIcon, GearIcon, ViewGridIcon } from "@radix-ui/react-icons";
import { usePropertyLayout, type PropertyLayout } from "./use-property-layout";
import type { CSSProperties } from "react";
import { UnstyledButton } from "@/shared/ui";
import { useAgencyPermissions } from "@/features/agency";
import { propertyTransactionTypes } from "@/shared/utils/property-display";
import type { PropertyGroup, GroupBy } from "./property-grouping";
import { navIconFor } from '@layout/agency/agency-nav';
import { FF_APP_SPREADSHEETS, FF_APP_PROPERTY_GROUPING } from "@/shared/utils/feature-flag";
import {
  ImportResultBanner,
  SpreadsheetMenu,
  useImportResultBanner,
} from "@features/spreadsheets";

export type FetchSettings = { pageSize: number; page?: number };

export { handleDeleteConfirmation, handleDeleteAction } from "./manage-properties-actions";

const ManageProperties: React.FC = () => {
  const { t } = useTranslation('features/properties');
  const { t: tSeo } = useTranslation('@pages/manage-properties');
  const updateNavigationBar = useLayout((state) => state.updateNavigationBar);

  const { useStore: useAgencyStore } = agencyContext;
  const { id: agency_id } = useAgencyStore((state) => state.agency);

  useEffect(() => {
    const propertiesPath = `/app/agencies/${agency_id}/properties`;
    const properties_navigate = (
      <SideMenuLink label={t('form.table.properties')} linkTo={propertiesPath} />
    );
    updateNavigationBar(properties_navigate, 1);
  }, []);

  return (
    /*
      * Five rows, not ten. The row is twice the height it was -- the cover
      * is now big enough to tell two flats apart at a glance -- so ten of
      * them no longer fit on a screen, and a page you have to scroll to
      * finish is a page whose size is not doing its job.
      */
    <CrudProvider initialState={{ fetchSettings: { pageSize: 5, page: 1 } }}>
      <PageSEO title={tSeo("pageTitle")} description="" />
      <PropertiesPageContent />
    </CrudProvider>
  );
};

const EMPTY_PROPERTY_FILTERS: PropertyFilters = {};

/**
 * The columns an export of this list carries, matching what the table shows.
 */
const PROPERTY_EXPORT_COLUMNS = [
  'code', 'title', 'category', 'transaction_type', 'neighborhood', 'price', 'status',
];

const PropertiesPageContent: React.FC = () => {
  const { t } = useTranslation('features/properties');
  const crud = useCrudStore((state) => state);
  const {
    isFormVisible, setIsFormVisible, setIsTableVisible, filtersOpen, setFiltersOpen, refreshFetch,
  } = crud;

  const navigate = useNavigate();
  const { canManagePropertySettings } = useAgencyPermissions();
  const { agency } = useAgency((state) => state);
  const agency_id = agency.id ?? '';
  const isMobile = useIsMobile();

  const { filters: advancedFilters, setFilters: setAdvancedFilters, resetFilters: clearAdvancedFilters } = useUrlFilters(EMPTY_PROPERTY_FILTERS);
  const activeFilterCount = useMemo(() => countActiveFilters(advancedFilters), [advancedFilters]);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchText = searchParams.get('q') ?? '';
  const updateSearch = useCallback((val: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val) next.set('q', val); else next.delete('q');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const propertiesFilters = useMemo(() => (searchText ? { search: searchText } : {}), [searchText]);

  // Grouping (behind FF_APP_PROPERTY_GROUPING). The dimension/view live in the
  // URL; the aggregates are computed client-side only once the FULL filtered
  // set is loaded, so medians are never taken from a partial page.
  const grouping = usePropertyGrouping();
  const groupingEnabled = FF_APP_PROPERTY_GROUPING;
  const activeGroup: GroupBy = groupingEnabled ? grouping.group : 'none';
  // The grid is a desktop view of the ungrouped list; read before the fetch,
  // since the grid asks the server for the wider card covers.
  const { layout, setLayout } = usePropertyLayout(!isMobile && activeGroup === 'none');

  const {
    properties, effectiveProperties, dataStateFlags, isRefreshing, isLoadingMore, totalCount,
    editingProperty, setEditingProperty, sentinelRef, scrollContainerRef, sortKey, sortDir, onSort,
    order, onOrderChange,
  } = usePropertiesData({
    crud, agencyId: agency_id, propertiesFilters, advancedFilters, isMobile,
    cover: layout === 'grid' ? 'card' : undefined,
  });

  const currentPage = crud.fetchSettings?.page ?? 1;
  const hasReachedEnd = !dataStateFlags.isLoading && !isLoadingMore
    && properties.length > 0 && currentPage >= crud.totalPages;
  const isFullyLoaded = !dataStateFlags.isLoading && !isLoadingMore && currentPage >= crud.totalPages;
  const groupingData = usePropertyGroupingData({
    group: activeGroup,
    properties,
    isComplete: isFullyLoaded,
    totalCount,
    t,
  });

  const handleEditProperty = (property: PropertyResponseType) => {
    setEditingProperty(property);
    setIsFormVisible(true);
  };

  const hasActiveFilters = Object.keys(buildAdvancedFilterQuery(advancedFilters)).length > 0 || Object.keys(propertiesFilters).length > 0;
  const filterChipLabels: string[] = buildFilterChipLabels(advancedFilters);

  const removeFilterByLabel = (label: string) => {
    setAdvancedFilters(removeAdvancedFilterByLabel(advancedFilters, label) as Partial<PropertyFilters>);
  };

  // The group funnel (and summary-card click) narrows the list through the
  // SAME filter pipeline as every other chip, then clears the grouping so the
  // user lands on the plain filtered list for that group. Both the filter
  // (`?f=`) and the grouping-exit (`?group`/`?view`) are written in ONE atomic
  // history entry via `applyGroupFilterAndExit` — three same-tick
  // `setSearchParams` calls across two hook instances used to race, and the
  // grouping-clear silently clobbered the just-set filter.
  const handleFilterGroup = (group: PropertyGroup) => {
    if (activeGroup === 'none') return;
    const transactionTypes = group.items.length
      ? propertyTransactionTypes(group.items[0] as unknown as Record<string, unknown>)
      : [];
    const patch = groupFilterPatch(activeGroup, group.label, transactionTypes, group.subLabel);
    grouping.applyGroupFilterAndExit(patch as Partial<PropertyFilters>, EMPTY_PROPERTY_FILTERS);
  };

  // The ordering menu travels with the grouping controls: both answer "how
  // is this list arranged", and the search row holds one trailing slot.
  const headerTrailingAction = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      {canManagePropertySettings && (
        <UnstyledButton
          type="button"
          data-testid="properties-settings-shortcut"
          onClick={() => navigate(`/app/agencies/${agency_id}/settings?section=imoveis`)}
          style={settingsShortcutStyle}
        >
          <GearIcon width={14} height={14} />
          {t('v2.imoveis.settingsShortcut', { ns: 'features/agency-settings' })}
        </UnstyledButton>
      )}
      <PageHeaderSortMenu testIdPrefix="properties" order={order} onChange={onOrderChange} />
      {groupingEnabled && <PageHeaderGroupByMenu group={grouping.group} onChange={grouping.setGroup} />}
      {activeGroup === 'none' && !isMobile && (
        <PageHeaderViewToggle<PropertyLayout>
          ariaLabel={t('list.layout.label')}
          value={layout}
          onChange={setLayout}
          testId="property-layout-toggle"
          items={[
            { value: 'list', label: t('list.layout.list'), icon: <RowsIcon width={14} height={14} />, testId: 'property-layout-list' },
            { value: 'grid', label: t('list.layout.grid'), icon: <ViewGridIcon width={14} height={14} />, testId: 'property-layout-grid' },
          ]}
        />
      )}
      {groupingEnabled && grouping.isGrouped && !isMobile && (
        <PageHeaderViewToggle<'list' | 'summary'>
          ariaLabel={t('listHeader.groupByMenuTitle')}
          value={grouping.view}
          onChange={grouping.setView}
          testId="group-view-toggle"
          items={[
            { value: 'list', label: t('list.grouping.viewList'), icon: <RowsIcon width={14} height={14} />, testId: 'group-view-list' },
            { value: 'summary', label: t('list.grouping.viewSummary'), icon: <DashboardIcon width={14} height={14} />, testId: 'group-view-summary' },
          ]}
        />
      )}
    </div>
  );

  const propertyColumns = buildPropertyColumns(t, handleEditProperty);

  const importBanner = useImportResultBanner({
    agencyId: agency_id,
    importFilter: advancedFilters.import_id ?? '',
    setImportFilter: (import_id) => setAdvancedFilters({ ...advancedFilters, import_id }),
    onRefresh: () => { refreshFetch(); },
  });

  const headerBar = (
    <StickyHeaderBar>
      <PageHeader
        title={t('list.title') || 'Imóveis'}
        icon={navIconFor('properties')}
        ctaLabel={t('list.newProperty') || 'Novo Imóvel'}
        secondaryAction={FF_APP_SPREADSHEETS ? (
          <SpreadsheetMenu
            entity="properties"
            agencyId={agency_id}
            totalCount={totalCount ?? properties.length}
            filters={advancedFilters}
            columns={PROPERTY_EXPORT_COLUMNS}
          />
        ) : undefined}
        searchPlaceholder={`${t('list.search') || 'Buscar'} imóvel...`}
        searchValue={searchText}
        onCtaClick={() => { setEditingProperty(null); setIsFormVisible(true); }}
        onSearchChange={updateSearch}
        onFiltersToggle={() => setFiltersOpen(true)}
        favoritesOnly={advancedFilters.favorites === true}
        onToggleFavorites={() => setAdvancedFilters({
          ...advancedFilters,
          favorites: advancedFilters.favorites === true ? undefined : true,
        })}
        filterCount={activeFilterCount}
        activeFilters={filterChipLabels}
        onRemoveFilter={(label) => removeFilterByLabel(label)}
        onClearAllFilters={() => { clearAdvancedFilters(); }}
        introLabel={t('intro.label')}
        introBody={t('intro.body')}
        introTestIdPrefix="properties"
        trailingAction={headerTrailingAction}
      />
    </StickyHeaderBar>
  );

  // On mobile, the agency layout's own scroll div (`flex-1 overflow-y-auto`) is the
  // single scroll owner. These wrappers must NOT clip or cap height there, otherwise
  // they become non-scrolling sticky-containers and the header detaches. On desktop
  // the nested overflow/min-height chain is preserved so the inner list scrolls and
  // the header stays fixed exactly as before.
  const { rootStyle, paddingStyle, stackStyle, scrollContainerClassName } = getListingPageLayout(isMobile);

  return (
    <div style={rootStyle}>
      <div style={paddingStyle}>
        <div style={stackStyle}>
          <>
            {!isMobile && !dataStateFlags.isError && !dataStateFlags.isLoading && headerBar}
            {FF_APP_SPREADSHEETS && importBanner.summary && (
              <ImportResultBanner
                entity="properties"
                summary={importBanner.summary}
                onlyThisImport={importBanner.onlyThisImport}
                onToggleOnlyThisImport={importBanner.toggleOnlyThisImport}
                onUndo={importBanner.undo}
                onDismiss={importBanner.dismiss}
              />
            )}
            <PropertiesListBody
              t={t}
              isMobile={isMobile}
              headerBar={headerBar}
              dataStateFlags={dataStateFlags}
              isRefreshing={isRefreshing}
              effectiveProperties={effectiveProperties}
              properties={properties}
              isLoadingMore={isLoadingMore}
              hasReachedEnd={hasReachedEnd}
              totalCount={totalCount}
              hasActiveFilters={hasActiveFilters}
              columns={propertyColumns}
              scrollContainerRef={scrollContainerRef}
              sentinelRef={sentinelRef}
              scrollContainerClassName={scrollContainerClassName}
              onRefresh={() => { refreshFetch(); }}
              onNewProperty={() => { setEditingProperty(null); setIsFormVisible(true); }}
              onRowNavigate={(prop) => navigate(`/app/agencies/${agency_id}/properties/${prop.id}`)}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              layout={layout}
              grouping={groupingEnabled && grouping.isGrouped ? {
                by: activeGroup as Exclude<GroupBy, 'none'>,
                view: grouping.view,
                groups: groupingData.groups,
                aggregates: groupingData.aggregates,
                total: groupingData.total,
                isCollapsed: grouping.isCollapsed,
                onToggleCollapsed: grouping.toggleCollapsed,
                isShowingAll: grouping.isShowingAll,
                onToggleShowAll: grouping.toggleShowAll,
                onExpandAll: grouping.expandAll,
                onCollapseAll: () => grouping.collapseAll(groupingData.groups.map((g) => g.key)),
                onFilterGroup: handleFilterGroup,
              } : undefined}
            />
          </>
        </div>
      </div>

      <PropertyDrawer
        open={isFormVisible}
        onClose={() => { setIsFormVisible(false); setIsTableVisible(true); setEditingProperty(null); }}
        agencyId={agency_id}
        editingProperty={editingProperty}
        onSuccess={() => { refreshFetch(); }}
      />

      <FilterPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={(filters) => {
          setAdvancedFilters(mapFilterPanelToStore(filters) as Partial<PropertyFilters>);
        }}
        onClear={() => { clearAdvancedFilters(); }}
      />
    </div>
  );
};

export default withAuthRedirect(ManageProperties);

/** Same outline as the Vendas and Locações shortcuts to their settings. */
const settingsShortcutStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--text-body-sm)',
  fontWeight: 'var(--font-medium)' as unknown as number,
  background: 'transparent',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-medium)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
  whiteSpace: 'nowrap',
};
